/**
 * @fileoverview Org portal — instructors list (Spec 11). Shows every instructor
 * in the org with a student count, an invite-by-email flow with a pending list,
 * and per-row remove. Each row links to the instructor detail page.
 *
 * A member who owes lesson notes says how many beside their students (C4),
 * read from `/org/notes-owed` beside the roster so the list never waits on it.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Card, SectionHeader, Button, ListRow, Avatar, Tag, IconButton } from "@/components/studio/primitives";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function OrgInstructorsPage() {
  const navigate = useNavigate();
  const [instructors, setInstructors] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [owed, setOwed] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    // Best effort: a roster without the counts is still the roster.
    orgApi
      .get("/org/notes-owed")
      .then((res) => setOwed(res.data?.instructors || {}))
      .catch(() => setOwed({}));
    try {
      const [list, inv] = await Promise.all([
        orgApi.get("/org/instructors"),
        orgApi.get("/org/instructors/invites"),
      ]);
      setInstructors(list.data.instructors || []);
      setInvites(inv.data.invites || []);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't load instructors"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      toast.error("Enter a valid email");
      return;
    }
    setInviting(true);
    try {
      await orgApi.post("/org/instructors/invite", { email: email.trim() });
      setEmail("");
      toast.success("Invitation sent");
      load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't send invite"));
    } finally {
      setInviting(false);
    }
  };

  const revoke = async (token) => {
    try {
      await orgApi.delete(`/org/instructors/invites/${token}`);
      load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't revoke invite"));
    }
  };

  const remove = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from the organization? They keep their account and data.`)) return;
    try {
      await orgApi.delete(`/org/instructors/${userId}`);
      toast.success("Instructor removed");
      load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't remove instructor"));
    }
  };

  // O5 retired seats: the org's plan covers every member, so there is nothing
  // per-instructor to assign here and no cap for an admin to run into.

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 md:px-8">
      <h1 className="mb-6 font-serif text-2xl font-semibold text-[var(--text-primary)]">Instructors</h1>

      {/* Invite */}
      <Card className="mb-6">
        <SectionHeader>Invite an instructor</SectionHeader>
        <form onSubmit={invite} className="flex flex-col gap-3 sm:flex-row">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teacher@email.com"
            data-testid="org-invite-email"
            className="flex-1 rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[11px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]"
          />
          <Button type="submit" disabled={inviting} icon="mail" data-testid="org-invite-submit">
            {inviting ? "Sending…" : "Send invite"}
          </Button>
        </form>

        {invites.length > 0 && (
          <div className="mt-4 border-t border-[var(--studio-divider)] pt-4">
            <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">Pending invitations</div>
            <div className="flex flex-col gap-2">
              {invites.map((inv) => (
                <div key={inv.token} className="flex items-center justify-between gap-3">
                  <span className="truncate font-sans text-[15px] text-[var(--text-secondary)]">{inv.email}</span>
                  <div className="flex items-center gap-2">
                    <Tag>Pending</Tag>
                    <button
                      type="button"
                      onClick={() => revoke(inv.token)}
                      className="font-sans text-[13px] font-medium text-[var(--state-danger)] hover:underline"
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Roster */}
      <Card padding={0}>
        {loading ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</div>
        ) : instructors.length === 0 ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">
            No instructors yet. Invite one above.
          </div>
        ) : (
          instructors.map((it, i) => (
              <ListRow
                key={it.user_id}
                borderBottom={i < instructors.length - 1}
                leading={<Avatar name={it.name} />}
                title={it.name || it.email}
                subtitle={it.email}
                meta={
                  <>
                    {`${it.student_count} student${it.student_count === 1 ? "" : "s"}`}
                    {owed[it.user_id] > 0 && (
                      <span className="font-semibold text-[var(--accent-yellow-text)]" data-testid={`instructor-owed-${it.user_id}`}>
                        {` · ${owed[it.user_id]} note${owed[it.user_id] === 1 ? "" : "s"} owed`}
                      </span>
                    )}
                  </>
                }
                onPress={() => navigate(`/org/instructors/${it.user_id}`)}
                trailing={
                  <div className="flex items-center gap-2">
                    <IconButton
                      icon="trash"
                      size={36}
                      danger
                      ariaLabel={`Remove ${it.name}`}
                      onPress={(e) => { e.stopPropagation?.(); remove(it.user_id, it.name || it.email); }}
                    />
                  </div>
                }
              />
          ))
        )}
      </Card>
    </div>
  );
}
